import type { StateCreator } from 'zustand'

import type { WorkspaceMember, WorkspaceMemberRole } from '../project-members.interface'
import { upsertPendingProjectInvite } from '../project-members.utils'

import { projectMembersInitialState } from './project-members.slice'
import type { ProjectMembersSlice, ProjectsStore } from './projects.type'

type StateUpdater<T> = T | ((current: T) => T)

type ProjectMembersSet = Parameters<StateCreator<ProjectsStore, [['zustand/immer', never]], [], ProjectsStore>>[0]

export const createProjectMembersActions = (
  set: ProjectMembersSet
): Omit<
  ProjectMembersSlice,
  'projectMembersError' | 'projectMembersPending' | 'projectMembersReadyProjectId' | 'projectMembersServer'
> => ({
  resetProjectMembers: () =>
    set((state) => {
      state.projectMembersServer = projectMembersInitialState.projectMembersServer
      state.projectMembersPending = projectMembersInitialState.projectMembersPending
      state.projectMembersReadyProjectId = projectMembersInitialState.projectMembersReadyProjectId
      state.projectMembersError = projectMembersInitialState.projectMembersError
    }),
  setProjectMembersServer: (membersOrUpdater: StateUpdater<WorkspaceMember[]>) =>
    set((state) => {
      state.projectMembersServer =
        typeof membersOrUpdater === 'function' ? membersOrUpdater(state.projectMembersServer) : membersOrUpdater
    }),
  setProjectMembersPending: (pendingOrUpdater: StateUpdater<WorkspaceMember[]>) =>
    set((state) => {
      state.projectMembersPending =
        typeof pendingOrUpdater === 'function' ? pendingOrUpdater(state.projectMembersPending) : pendingOrUpdater
    }),
  upsertProjectMemberPendingInvite: (email: string, role: WorkspaceMemberRole, projectId: string) =>
    set((state) => {
      state.projectMembersPending = upsertPendingProjectInvite(
        state.projectMembersPending,
        state.projectMembersServer,
        email,
        role,
        projectId
      )
    }),
  loadProjectMembers: async (projectId: string) => {
    set((state) => {
      state.projectMembersReadyProjectId = null
      state.projectMembersPending = []
      state.projectMembersError = null
    })

    try {
      const { getProjectMembers } = await import('../projects')
      const data = await getProjectMembers(projectId)
      set((state) => {
        state.projectMembersServer = data
        state.projectMembersReadyProjectId = projectId
      })
    } catch (err) {
      set((state) => {
        state.projectMembersServer = []
        state.projectMembersError = err instanceof Error ? err.message : 'Failed to load members'
        state.projectMembersReadyProjectId = projectId
      })
    }
  },
})
